import React, { FormEvent, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import Faq from './support/faq';
import { SignInFaq } from './sign-in-faq';
import Logo from '../../public/images/logo.svg';
import SignUpPrivacyTrust from './sign-up-trust';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);

  const openCrispChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
      window.$crisp.push(["set", "message:text", ["I need help resetting my password "]]);
    }
  };

  const handleGoogleAuth = () => {
    if (!agreeToPrivacy) {
      toast.warning('Please agree to our privacy policy to continue.');
      setSigningUp(false);
      return;
    }
    // Redirect to Google authentication
    window.location.href = '/api/auth/google/login';
  };


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSigningUp(true);

    if (password !== confirmPassword) {
      alert('Passwords do not match');
      setSigningUp(false);
      return;
    }

    if (!agreeToPrivacy) {
      toast.warning('Please agree to our privacy policy to continue.');
      setSigningUp(false);
      return;
    }

    try {
      await axios.post('/api/auth/signup', { email, password });
      setSignupSuccess(true);
      setSigningUp(false);
    } catch (error) {
      setSigningUp(false);
      if (error instanceof AxiosError) {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          const errorMessage = error.response.data?.message || 'Signup failed. Please try again.';
          toast.warning(errorMessage, { position: 'top-center' });
        } else if (error.request) {
          // The request was made but no response was received
          toast.warning('No response received from the server. Please try again later.');
        } else {
          // Something happened in setting up the request that triggered an Error
          toast.warning('Error occurred while setting up the request.');
        }
      } else {
        toast.error('An unknown error occurred.');
        throw new Error('An unknown error occurred.');
      }
    }
  };

  if (signupSuccess) {
    return (
      <div className='flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8'>
        <div className='sm:mx-auto sm:w-full sm:max-w-md'>
          <img
            className='mx-auto h-10 w-auto'
            src={Logo}
            alt='Neoface Logo'
          />
          <h2 className='mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900'>
            Check your email
          </h2>
          <p className='mt-4 text-center text-base text-gray-600'>
            A confirmation email has been sent to {email}. Please follow the instructions to complete your signup.
          </p>
          <p className='mt-2 text-center text-sm text-gray-600'>
            <b>If you don't see our email, please check your spam folder or</b> <a href='#' onClick={openCrispChat} className='font-medium text-indigo-600 hover:text-indigo-500'>contact
            support</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 px-2'>
        <div className='sm:mx-auto sm:w-full sm:max-w-md'>
          <img
            className='mx-auto h-10 w-auto'
            src={Logo}
            alt='Neoface Logo'
          />
          <h2 className='mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900'>
            Create an account.
            <p> It's Free!</p>
          </h2>
          <div className="mt-2 flex justify-center items-center">
            <ShieldCheckIcon className="h-5 w-5 text-indigo-600 mr-1" />
            <p className="text-sm text-gray-600">Your Identity. Your Control.</p>
          </div>
        </div>
        <div className='mt-10 mx-auto max-w-[480px] w-full'>
          <div className='bg-white px-6 py-10 shadow sm:rounded-lg sm:px-12'>
            {/* Google Sign-Up Button */}
            <div className='mb-6'>
              <a
                href='/api/auth/google/login'
                onClick={(e) => {
                  if (!agreeToPrivacy) {
                    e.preventDefault();
                    toast.warning('Please agree to our privacy policy to continue.');
                  }
                }}
                className='flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2.5 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-offset-0 transition-colors duration-200'
              >
                <svg className='h-5 w-5' aria-hidden='true' viewBox='0 0 24 24'>
                  <path
                    d='M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z'
                    fill='#EA4335'
                  />
                  <path
                    d='M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z'
                    fill='#4285F4'
                  />
                  <path
                    d='M5.26498 14.2949C5.02498 13.5699 4.88495 12.7999 4.88495 11.9999C4.88495 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z'
                    fill='#FBBC05'
                  />
                  <path
                    d='M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.25 12.0004 19.25C8.8704 19.25 6.2154 17.14 5.2704 14.295L1.27539 17.39C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z'
                    fill='#34A853'
                  />
                </svg>
                <span>Continue with Google</span>
              </a>
            </div>

            {/* Divider */}
            <div className='relative mt-8 mb-8'>
              <div className='absolute inset-0 flex items-center' aria-hidden='true'>
                <div className='w-full border-t border-gray-300' />
              </div>
              <div className='relative flex justify-center text-sm font-medium leading-6'>
                <span className='bg-white px-6 text-gray-500'>Or continue with email</span>
              </div>
            </div>

            <form className='space-y-6' onSubmit={handleSubmit}>
              <div>
              <label htmlFor='email' className='block text-sm font-medium leading-6 text-gray-900'>
                  Email address
                </label>
                <div className='mt-2'>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    required
                    className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor='password' className='block text-sm font-medium leading-6 text-gray-900'>
                  Password
                </label>
                <div className='mt-2'>
                  <input
                    id='password'
                    name='password'
                    type='password'
                    autoComplete='new-password'
                    required
                    className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor='confirmPassword' className='block text-sm font-medium leading-6 text-gray-900'>
                  Confirm Password
                </label>
                <div className='mt-2'>
                  <input
                    id='confirmPassword'
                    name='confirmPassword'
                    type='password'
                    autoComplete='new-password'
                    required
                    className='block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="privacy"
                    name="privacy"
                    type="checkbox"
                    checked={agreeToPrivacy}
                    onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="privacy" className="text-gray-500">
                    I agree to the&nbsp;
                    <a href="https://neoface.ai/privacy" className="text-indigo-600 hover:text-indigo-500">Privacy
                      Policy</a>
                    &nbsp;and&nbsp;
                    <a href="https://neoface.ai/terms" className="text-indigo-600 hover:text-indigo-500">Terms of
                      Use</a>.
                    <br/>
                    <b>Your original face is never permanently stored.</b>
                  </label>
                </div>
              </div>

              <div>
                <button
                  type='submit'
                  disabled={signingUp}
                  className='flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                >
                  {signingUp ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Signing up...</span>
                    </div>
                  ) : (
                    'Sign up - 100% Private'
                  )}
                </button>
              </div>
            </form>
          </div>

          <p className='mt-7  text-center text-sm text-gray-500'>
            Already have an account?{' '}
            <Link to='/sign-in' className='font-semibold leading-6 text-indigo-600 hover:text-indigo-500'>
              Sign in!
            </Link>
            {' '}
          </p>

          {/* New Privacy Trust Component */}
          <div className="flex justify-center mb-8">
            <SignUpPrivacyTrust />
          </div>
        </div>
      </div>
      <div className='bg-gray-50 py-12'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl mx-auto'>
            <Faq questions={SignInFaq} />
          </div>
        </div>
      </div>
    </>
  );
}